'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import type { CSSProperties, ReactNode } from 'react'
import { ALL_LESSONS, formatMinutes, MODULES, TOTAL_LESSONS, TOTAL_MINUTES } from '../_lib/curriculum'
import { currentStreak, learnerLoss } from '../_lib/progress'
import { useHydrated, useProgress } from '../_lib/progress-client'
import GdHeader from './GdHeader'
import { IconArrowRight, IconCode, IconFlame, IconMap, IconSpark, IconSync } from './icons'
import { useInView } from './widgets/controls'

const HeroLandscape = dynamic(() => import('./HeroLandscape'), { ssr: false })

const TOTAL_HOURS = Math.round(TOTAL_MINUTES / 60)
const PROJECTS = ALL_LESSONS.filter(l => l.kinds.includes('project')).length

function Reveal({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }) {
  const [ref, seen] = useInView<HTMLDivElement>('-40px')
  return (
    <div ref={ref} className={`gd-reveal ${className}`} data-visible={seen} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  )
}

function useNextLesson() {
  const state = useProgress()
  const hydrated = useHydrated()
  const firstOpen = ALL_LESSONS.find(l => !state.done[l.slug]) ?? null
  const started = hydrated && (Object.keys(state.done).length > 0 || Object.values(state.steps).some(s => s.i > 0))
  return { next: firstOpen, started, hydrated }
}

function Hero() {
  const { next, started } = useNextLesson()
  return (
    <section className="relative overflow-hidden" style={{ minHeight: 'min(880px, calc(100svh - 60px))' }}>
      <div className="pointer-events-none absolute inset-y-0 right-[-18%] w-[118%] opacity-95 md:right-[-6%] md:w-[72%]">
        <HeroLandscape />
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'linear-gradient(90deg, var(--gd-bg) 0%, color-mix(in srgb, var(--gd-bg) 82%, transparent) 38%, transparent 70%)' }}
      />
      <div className="relative mx-auto flex max-w-[1240px] flex-col justify-center px-6 pb-16 pt-20 sm:px-10 md:min-h-[min(880px,calc(100svh-60px))] md:pt-10">
        <p className="gd-label gd-hero-in" style={{ '--d': '0ms' } as CSSProperties}>
          A free course · no accounts · no ads · no paywall
        </p>
        <h1 className="gd-display gd-hero-in mt-6 max-w-[11ch] text-[clamp(56px,9.6vw,128px)]" style={{ '--d': '80ms' } as CSSProperties}>
          Learn AI from <em>zero</em> to the frontier.
        </h1>
        <p className="gd-hero-in mt-7 max-w-[46ch] text-[19px] leading-[1.6] sm:text-[21px]" style={{ '--d': '180ms', color: 'var(--gd-ink-2)' } as CSSProperties}>
          Start knowing no math and no code. Finish by training your own GPT — in your browser. Every equation built from
          scratch, every idea something you can drag, run and break.
        </p>
        <div className="gd-hero-in mt-10 flex flex-wrap items-center gap-3" style={{ '--d': '280ms' } as CSSProperties}>
          {started && next ? (
            <>
              <Link href={`/learn/${next.slug}`} className="gd-btn gd-btn-primary !h-auto min-h-[52px] max-w-full !whitespace-normal !px-7 py-3 text-left !text-[16px] !leading-snug">
                <span>Continue: {next.title}</span> <IconArrowRight size={18} className="shrink-0" />
              </Link>
              <a href="#map" className="gd-btn gd-btn-ghost !h-[52px] !px-6">
                <IconMap size={17} /> Course map
              </a>
            </>
          ) : (
            <>
              <Link href={`/learn/${ALL_LESSONS[0].slug}`} className="gd-btn gd-btn-primary !h-[52px] !px-7 !text-[16px]">
                Start at zero <IconArrowRight size={18} />
              </Link>
              <a href="#map" className="gd-btn gd-btn-ghost !h-[52px] !px-6">
                <IconMap size={17} /> See all {TOTAL_LESSONS} lessons
              </a>
            </>
          )}
        </div>
        <dl className="gd-hero-in mt-14 grid max-w-[560px] grid-cols-3 gap-6" style={{ '--d': '380ms' } as CSSProperties}>
          {[
            [String(TOTAL_LESSONS), 'lessons'],
            [`~${TOTAL_HOURS}h`, `of hands-on work · ${PROJECTS} projects`],
            ['0', 'prerequisites'],
          ].map(([v, l]) => (
            <div key={l}>
              <dt className="gd-serif text-[44px] leading-none">{v}</dt>
              <dd className="gd-label mt-2 !text-[10px]">{l}</dd>
            </div>
          ))}
        </dl>
      </div>
      <p className="gd-label absolute bottom-5 right-6 hidden !text-[10px] md:block" aria-hidden>
        fig. 0 — a ball doing gradient descent on a loss landscape
      </p>
    </section>
  )
}

const PRINCIPLES: { icon: ReactNode; title: string; body: string; color: string }[] = [
  {
    icon: <IconSpark />,
    title: 'Math from the first step',
    body: 'Never seen an equation? Perfect. Every symbol is introduced with a picture you can move, in plain words — from the number line up to gradients and attention.',
    color: 'var(--gd-blue)',
  },
  {
    icon: <IconCode />,
    title: 'A real TypeScript workbench',
    body: 'A full code editor with autocomplete and type-checking runs right in the page. You write every model yourself — no Python, no installs, no AI doing it for you.',
    color: 'var(--gd-green)',
  },
  {
    icon: <IconFlame />,
    title: 'Tiny models, trained live',
    body: 'Spam filters, neural nets, a name generator, a small GPT: all trained on your own device while you watch the loss fall. Nothing is a video.',
    color: 'var(--gd-accent)',
  },
  {
    icon: <IconSync />,
    title: 'Yours, everywhere, privately',
    body: 'Progress saves as you go and syncs across devices with a single key — no email, no password, no tracking. Free forever.',
    color: 'var(--gd-violet)',
  },
]

function Principles() {
  return (
    <section className="mx-auto max-w-[1240px] px-6 py-24 sm:px-10">
      <Reveal>
        <p className="gd-label">How it works</p>
        <h2 className="gd-display mt-4 max-w-[18ch] text-[clamp(40px,5.6vw,68px)]">
          Not a wall of text. <em>A lab.</em>
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PRINCIPLES.map((p, i) => (
          <Reveal key={p.title} delay={i * 80}>
            <div className="gd-card h-full p-6">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full" style={{ background: `color-mix(in srgb, ${p.color} 13%, transparent)`, color: p.color }}>
                {p.icon}
              </span>
              <h3 className="mt-5 text-[19px] font-semibold leading-snug tracking-[-0.01em]">{p.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed" style={{ color: 'var(--gd-ink-2)' }}>
                {p.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  )
}

function ProgressStrip() {
  const state = useProgress()
  const hydrated = useHydrated()
  if (!hydrated) return null
  const done = Object.keys(state.done).length
  if (!done && !Object.keys(state.steps).length) return null
  const streak = currentStreak(state)
  return (
    <div className="gd-card mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 px-6 py-5">
      <div>
        <p className="gd-label !text-[10px]">Your loss</p>
        <p className="gd-serif text-[34px] leading-none">{learnerLoss(done, TOTAL_LESSONS).toFixed(3)}</p>
      </div>
      <div>
        <p className="gd-label !text-[10px]">Lessons done</p>
        <p className="gd-serif text-[34px] leading-none">
          {done}
          <span className="text-[20px]" style={{ color: 'var(--gd-ink-3)' }}>
            {' '}
            / {TOTAL_LESSONS}
          </span>
        </p>
      </div>
      <div>
        <p className="gd-label !text-[10px]">Streak</p>
        <p className="gd-serif text-[34px] leading-none">
          {streak}
          <span className="text-[20px]" style={{ color: 'var(--gd-ink-3)' }}>
            {' '}
            day{streak === 1 ? '' : 's'}
          </span>
        </p>
      </div>
      <div className="min-w-[200px] flex-1">
        <div className="h-[6px] overflow-hidden rounded-full" style={{ background: 'var(--gd-bg-2)' }}>
          <div className="h-full rounded-full" style={{ width: `${(done / TOTAL_LESSONS) * 100}%`, background: 'var(--gd-accent)', transition: 'width 0.8s var(--gd-ease)' }} />
        </div>
      </div>
    </div>
  )
}

function CourseMap() {
  const state = useProgress()
  const hydrated = useHydrated()
  return (
    <section id="map" className="mx-auto max-w-[1240px] scroll-mt-20 px-6 pb-24 sm:px-10">
      <Reveal>
        <p className="gd-label">The course map</p>
        <h2 className="gd-display mt-4 max-w-[20ch] text-[clamp(40px,5.6vw,68px)]">
          Eleven modules, <em>one long descent.</em>
        </h2>
        <p className="mt-5 max-w-[60ch] text-[18px] leading-relaxed" style={{ color: 'var(--gd-ink-2)' }}>
          Each module leans only on the ones before it, and Modules 1–9 each end with a project. Skip ahead if you already know something —
          every lesson works on its own — but if you start at zero, you’ll never hit a wall.
        </p>
        <p className="mt-3 max-w-[60ch] text-[15.5px] leading-relaxed" style={{ color: 'var(--gd-ink-3)' }}>
          Honest pacing: about {TOTAL_HOURS} hours of real work for a complete beginner. At five hours a week that’s roughly{' '}
          {Math.round(TOTAL_HOURS / 5 / 4.3)} months — about the same as a university course, without the tuition.
        </p>
      </Reveal>
      <ProgressStrip />
      <div className="mt-12">
        {MODULES.map(m => {
          const done = m.lessons.filter(l => state.done[l.slug]).length
          return (
            <div key={m.id} id={`module-${m.id}`} className="gd-module scroll-mt-20">
              <Reveal>
                <div className="md:sticky md:top-24">
                  <span className="gd-module-num">{String(m.n).padStart(2, '0')}</span>
                  <h3 className="gd-serif mt-3 text-[34px] leading-[1.05]">{m.title}</h3>
                  <p className="mt-2 text-[15.5px] leading-relaxed" style={{ color: 'var(--gd-ink-2)' }}>
                    {m.tagline}
                  </p>
                  <p className="gd-label mt-4 !text-[10px]" suppressHydrationWarning>
                    {hydrated && done ? `${done} of ${m.lessons.length} done` : `${m.lessons.length} lessons · ~${formatMinutes(m.lessons.reduce((s, l) => s + l.minutes, 0))}`}
                  </p>
                </div>
              </Reveal>
              <div>
                {m.lessons.map((l, i) => {
                  const isDone = hydrated && Boolean(state.done[l.slug])
                  const step = hydrated ? state.steps[l.slug]?.i ?? -1 : -1
                  const started = !isDone && step >= 0
                  return (
                    <Reveal key={l.slug} delay={i * 40}>
                      <Link href={`/learn/${l.slug}`} className="gd-lesson-row">
                        <span className="gd-lesson-dot" data-state={isDone ? 'done' : started ? 'started' : undefined} style={{ '--gd-pct': `${Math.min(90, (step + 1) * 12)}%` } as CSSProperties}>
                          {isDone ? '✓' : `${m.n}.${i + 1}`}
                        </span>
                        <span className="min-w-0">
                          <span className="gd-lesson-title block">{l.title}</span>
                          <span className="gd-lesson-summary block">{l.summary}</span>
                        </span>
                        <span className="gd-label hidden pt-1 !text-[10px] sm:block">{l.kinds.includes('project') ? `project · ${formatMinutes(l.minutes)}` : formatMinutes(l.minutes)}</span>
                      </Link>
                    </Reveal>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="border-t px-6 py-16 sm:px-10" style={{ borderColor: 'var(--gd-rule)' }}>
      <div className="mx-auto grid max-w-[1240px] gap-10 md:grid-cols-[1.4fr_1fr]">
        <div>
          <h2 className="gd-display text-[clamp(36px,5vw,56px)]">
            Free. <em>Forever.</em>
          </h2>
          <p className="mt-4 max-w-[52ch] text-[16px] leading-relaxed" style={{ color: 'var(--gd-ink-2)' }}>
            No ads, no subscriptions, no accounts, no tracking. Your progress is stored on your device, and — only if you finish
            something — under an anonymous sync key so you can continue elsewhere. That’s all we keep.
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 md:items-end md:text-right">
          <Link href="/lab" className="gd-btn gd-btn-ghost">
            <IconCode size={16} /> Open the Lab
          </Link>
          <a href="https://ariesspring.dev" className="gd-link mt-4 text-[14px]">
            Made by Aries Spring ↗
          </a>
          <p className="gd-label !text-[10px]">© {new Date().getFullYear()} · Gradient</p>
        </div>
      </div>
    </footer>
  )
}

export default function Landing() {
  return (
    <>
      <GdHeader />
      <main id="main-content">
        <Hero />
        <Principles />
        <CourseMap />
      </main>
      <Footer />
    </>
  )
}
